/**
 * Solo Screen — Single-Player-Modus.
 * Volle Spielschleife: Puzzle → Antwort → Feedback → Nächstes.
 */
import { div, h2, h3, p, button, el } from '../../utils/domHelpers.js';
import { router } from '../../core/router.js';
import { puzzleService } from '../../services/puzzleService.js';
import { storageService } from '../../services/storageService.js';
import { createNumpad, bindNumpadKeyboard } from '../components/numpad.js';
import { toast } from '../components/toast.js';
import { getCategoryLabel, getDifficultyLabel } from '../../utils/formatters.js';

export function renderSoloScreen() {
  let currentAnswer = '';
  let answerDisplay, hudScore, hudStreak, puzzleEl, hintEl;
  let unbindKeyboard = null;

  // Cleanup beim Verlassen — wichtig damit Keyboard-Listener nicht stapeln
  const cleanup = () => unbindKeyboard?.();
  document.addEventListener('route:before-change', cleanup, { once: true });

  function renderPuzzle() {
    const puzzle = puzzleService.nextPuzzle();
    currentAnswer = '';
    answerDisplay.textContent = '_';
    hintEl.textContent = '';
    puzzleEl.innerHTML = '';

    puzzleEl.appendChild(
      el('div', { class: 'flex gap-2', style: { marginBottom: '12px' } }, [
        el('span', { class: 'badge' }, [getCategoryLabel(puzzle.cat)]),
        el('span', { class: 'badge badge--magenta' }, [getDifficultyLabel(puzzle.diff)]),
      ])
    );
    puzzleEl.appendChild(el('div', { class: 'puzzle-display' }, [puzzle.q]));
  }

  function appendDigit(d) {
    if (currentAnswer.length >= 10) return;
    currentAnswer += d;
    answerDisplay.textContent = currentAnswer || '_';
  }

  function deleteDigit() {
    currentAnswer = currentAnswer.slice(0, -1);
    answerDisplay.textContent = currentAnswer || '_';
  }

  function submit() {
    if (currentAnswer.length === 0) return toast.error('Antwort eingeben!');

    const result = puzzleService.validate(currentAnswer);
    storageService.recordSoloAttempt(result.correct, result.timeMs);

    if (result.correct) {
      answerDisplay.classList.add('flash-ok');
      hudScore.textContent = puzzleService.score;
      hudStreak.textContent = puzzleService.streak;
      storageService.setSoloHighScore(puzzleService.score);
      toast.success(`+${result.points} Punkte!`);
      setTimeout(() => { answerDisplay.classList.remove('flash-ok'); renderPuzzle(); }, 1000);
    } else {
      answerDisplay.classList.add('shake', 'flash-err');
      toast.error(`Falsch! ${result.explanation}`);
      setTimeout(() => answerDisplay.classList.remove('shake', 'flash-err'), 600);
    }
  }

  function showHint() {
    const hint = puzzleService.revealHint();
    if (hint) hintEl.textContent = `💡 ${hint}`;
  }

  // === Layout aufbauen ===
  hudScore  = el('span', { class: 'hud-value mono' }, [String(puzzleService.score)]);
  hudStreak = el('span', { class: 'hud-value mono' }, [String(puzzleService.streak)]);
  answerDisplay = el('div', { class: 'answer-display' }, ['_']);
  puzzleEl  = el('div', { class: 'flex-col flex-center w-full' });
  hintEl    = el('p', { class: 'mono', style: { color: 'var(--magenta)', minHeight: '24px', textAlign: 'center' } });

  const numpad = createNumpad({
    onInput:  appendDigit,
    onDelete: deleteDigit,
    onSubmit: submit,
  });
  unbindKeyboard = bindNumpadKeyboard({ onInput: appendDigit, onDelete: deleteDigit, onSubmit: submit });

  const panel = el('div', { class: 'glass-panel glass-panel--wide fade-up' }, [
    div({ class: 'game-hud' }, [
      div([ el('span', { class: 'hud-item' }, ['Score ']), hudScore ]),
      div([ el('span', { class: 'hud-item' }, ['Streak ']), hudStreak ]),
      button({ class: 'btn btn-ghost btn-sm', onclick: () => router.navigate('/') }, ['← Home']),
    ]),

    puzzleEl,
    answerDisplay,
    hintEl,
    numpad,

    div({ class: 'flex-center gap-3', style: { marginTop: '16px' } }, [
      button({ class: 'btn btn-ghost btn-sm', onclick: showHint }, ['💡 Hint (−100)']),
      button({ class: 'btn btn-ghost btn-sm', onclick: () => renderPuzzle() }, ['↻ Skip']),
    ]),
  ]);

  // Erstes Puzzle laden (deferred bis Panel im DOM)
  setTimeout(renderPuzzle, 0);

  return panel;
}
